import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeftIcon, PencilIcon, CheckIcon, XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TagSelector } from '@/components/TagSelector';
import type { Tag } from '@/components/TagBadge';
import { useI18n } from '@/hooks/useI18n';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog";
import { formatCurrency, getCurrencySymbol } from '@/utils/formatters';

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone_number: string | null;
    tax_id: string | null;
    bank_account_number: string | null;
    user_id: string;
    manager_id: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    user?: {
        id: string;
        email: string;
        is_active: boolean;
        is_admin?: boolean;
        cost_center_id?: string | null;
        roles?: string[];
        permissions?: string[];
        modules?: string[];
    };
    manager?: {
        id: string;
        first_name: string;
        last_name: string;
    };
    contracts?: EmploymentContract[];
    groups?: EmployeeGroup[];
}

interface JobAssignment {
    id: string;
    job_id: string;
    job_level_id: string;
}

interface TimesheetJobLevelRate {
    id: string;
    organization_id: string;
    job_level_id: string;
    taxation_type: 'full_time_employee' | 'daily_employee' | 'student';
    hourly_rate: number | null;
    tip_amount: number | null;
    created_at: string;
    updated_at?: string;
}

interface TimesheetJobLevel {
    id: string;
    job_id: string;
    level_name: 'Junior' | 'Experienced' | 'Senior';
    hourly_rate: number;
    is_default: boolean;
    rates?: TimesheetJobLevelRate[];
}

interface TimesheetJob {
    id: string;
    title: string;
    code?: string;
    levels?: TimesheetJobLevel[];
}

interface EmployeeGroup {
    id: string;
    name: string;
    description: string | null;
}

interface CostCenter {
    id: string;
    name: string;
}

interface Company {
    id: string;
    name: string;
    tax_id: string | null;
    is_active: boolean;
}

interface ContractFormData {
    employee_id: string;
    company_id: string;
    taxation_type: 'full_time_employee' | 'daily_employee' | 'student';
    default_cost_center_id: string;
    start_date: string;
    end_date: string;
    monthly_salary: string;
    is_active: boolean;
}

interface JobAssignmentFormData {
    job_id: string;
    job_level_id: string;
    cost_center_id: string;
}

interface ContractJobAssignment {
    id?: string;
    job_id: string;
    job_level_id: string;
    cost_center_id?: string | null;
}

interface EmploymentContract {
    id: string;
    employee_id: string;
    company_id: string;
    taxation_type: 'full_time_employee' | 'daily_employee' | 'student';
    default_cost_center_id: string | null;
    start_date: string;
    end_date: string | null;
    currency: string;
    monthly_salary?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    employee?: Employee;
    default_cost_center?: CostCenter;
    job_assignments: ContractJobAssignment[];
}

function EmployeeDetailPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const { employeeId } = params;
    const { t } = useI18n();

    const [employee, setEmployee] = useState<Employee | null>(null);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
    const [jobs, setJobs] = useState<TimesheetJob[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [baseCurrency, setBaseCurrency] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [contractError, setContractError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [employeeTags, setEmployeeTags] = useState<Tag[]>([]);
    const [canManageEmployeeAccess, setCanManageEmployeeAccess] = useState(false);

    // Contract dialog state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [editingContract, setEditingContract] = useState<EmploymentContract | null>(null);
    const [contractFormData, setContractFormData] = useState<ContractFormData>({
        employee_id: '',
        company_id: '',
        taxation_type: 'full_time_employee',
        default_cost_center_id: '',
        start_date: '',
        end_date: '',
        monthly_salary: '',
        is_active: true
    });
    const [jobAssignments, setJobAssignments] = useState<JobAssignmentFormData[]>([]);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        tax_id: '',
        bank_account_number: '',
        manager_id: '',
        is_active: true,
        is_admin: false,
        cost_center_id: '',
        roles: [] as string[],
        permissions: [] as string[],
        modules: [] as string[],
    });
    const availableRoles = [
        { value: 'barista', label: 'Barista' },
        { value: 'manager', label: 'Manager' },
        { value: 'org_admin', label: 'Organization admin' },
    ];
    const availablePermissions = [
        { value: 'finance.invoices.view', label: 'Finance: view invoices' },
        { value: 'finance.invoices.upload', label: 'Finance: upload invoices' },
        { value: 'finance.invoices.edit', label: 'Finance: edit invoices' },
        { value: 'finance.invoices.lifecycle.manage', label: 'Finance: manage invoice lifecycle' },
        { value: 'finance.nav.sync', label: 'Finance: NAV sync' },
        { value: 'finance.bank.reconcile', label: 'Finance: bank reconcile' },
        { value: 'finance.reports.view', label: 'Finance: view reports' },
        { value: 'inventory.view', label: 'Inventory: view' },
        { value: 'inventory.deliveries.record', label: 'Inventory: record deliveries' },
        { value: 'inventory.adjustments.record', label: 'Inventory: record adjustments' },
        { value: 'inventory.master_data.manage', label: 'Inventory: manage master data' },
        { value: 'people.self.view_profile', label: 'People: self view profile' },
        { value: 'people.self.edit_profile', label: 'People: self edit profile' },
        { value: 'people.self.view_schedule', label: 'People: self view schedule' },
        { value: 'people.self.view_timesheets', label: 'People: self view timesheets' },
        { value: 'people.self.request_time_off', label: 'People: self request time off' },
        { value: 'people.team.view_basic_info', label: 'People: team view basic info' },
        { value: 'people.team.view_schedule', label: 'People: team view schedule' },
        { value: 'people.team.view_timesheets', label: 'People: team view timesheets' },
        { value: 'people.team.manage_schedule', label: 'People: team manage schedule' },
        { value: 'people.team.approve_time_off', label: 'People: team approve time off' },
        { value: 'people.org.manage_employees', label: 'People: org manage employees' },
        { value: 'people.org.manage_policies', label: 'People: org manage policies' },
        { value: 'people.org.view_all', label: 'People: org view all' },
        { value: 'analytics.daily_stats.view', label: 'Analytics: view daily stats' },
        { value: 'analytics.full.view', label: 'Analytics: full view' },
        { value: 'org.settings.manage', label: 'Org: manage settings' },
        { value: 'org.cost_centers.manage', label: 'Org: manage cost centers' },
        { value: 'org.categories.manage', label: 'Org: manage categories' },
        { value: 'org.integrations.manage', label: 'Org: manage integrations' },
    ];
    const availableModules = [
        { value: 'expenses', label: 'Expenses module', description: 'Access to finance costs and expense management screens.' },
        { value: 'revenues', label: 'Revenues module', description: 'Access to sales invoices, orders, products, and revenue flows.' },
        { value: 'transactions', label: 'Transactions module', description: 'Access to payments, reconciliation, and bank transaction pages.' },
        { value: 'reports', label: 'Reports module', description: 'Access to dashboard, profit/loss, and metrics reporting pages.' },
        { value: 'people', label: 'People module', description: 'Access to employee, scheduler, timesheet, and time-off areas.' },
        { value: 'inventory', label: 'Inventory module', description: 'Access to inventory, ingredients, transactions, and batch flows.' },
        { value: 'menus', label: 'Menus module', description: 'Access to menu items and related menu management pages.' },
    ];

    useEffect(() => {
        fetchEmployee();
        fetchCostCenters();
        fetchAllEmployees();
        fetchJobs();
        fetchCompanies();
        fetchBaseCurrency();
        fetchCurrentUserAccess();
        loadEmployeeTags();
    }, [employeeId]);

    const fetchCurrentUserAccess = async () => {
        try {
            const response = await apiFetch('/api/auth/me');
            if (!response.ok) return;
            const me = await response.json();
            setCanManageEmployeeAccess(Boolean(me.can_manage_employee_access));
        } catch (err) {
            console.error('Error fetching current user permissions:', err);
        }
    };

    const loadEmployeeTags = async () => {
        try {
            const response = await apiFetch(`/api/tags/entities/employees/${employeeId}`);
            if (response.ok) {
                const data = await response.json();
                setEmployeeTags(data.map((item: any) => item.tag));
            }
        } catch (error) {
            console.error('Failed to load employee tags:', error);
        }
    };

    useEffect(() => {
        if (employee) {
            setFormData({
                first_name: employee.first_name,
                last_name: employee.last_name,
                email: employee.email || '',
                phone_number: employee.phone_number || '',
                tax_id: employee.tax_id || '',
                bank_account_number: employee.bank_account_number || '',
                manager_id: employee.manager_id || '',
                is_active: employee.is_active,
                is_admin: employee.user?.is_admin || false,
                cost_center_id: employee.user?.cost_center_id || '',
                roles: employee.user?.roles || [],
                permissions: employee.user?.permissions || [],
                modules: employee.user?.modules || [],
            });
        }
    }, [employee]);

    const fetchEmployee = async () => {
        try {
            setLoading(true);
            const response = await apiFetch(`/api/people/employees/${employeeId}/detail`);
            if (response.ok) {
                const data = await response.json();
                setEmployee(data);
                if (data?.user?.id) {
                    await fetchUserRoles(data.user.id);
                }
            } else {
                throw new Error('Failed to fetch employee');
            }
        } catch (err) {
            console.error('Error fetching employee:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch employee');
        } finally {
            setLoading(false);
        }
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(Array.isArray(data) ? data : (data.cost_centers || []));
            }
        } catch (err) {
            console.error('Error fetching cost centers:', err);
        }
    };

    const fetchAllEmployees = async () => {
        try {
            const response = await apiFetch('/api/people/employees/?limit=100&is_active=true');
            if (response.ok) {
                const data = await response.json();
                setAllEmployees(data.employees || []);
            }
        } catch (err) {
            console.error('Error fetching employees:', err);
        }
    };
    const fetchJobs = async () => {
        try {
            const response = await apiFetch('/api/timesheet/jobs?per_page=100');
            if (response.ok) {
                const data = await response.json();
                setJobs(data.jobs || []);
            }
        } catch (err) {
            console.error('Error fetching jobs:', err);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await apiFetch('/api/companies/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setCompanies(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
        }
    };

    const fetchBaseCurrency = async () => {
        try {
            const response = await apiFetch('/api/currencies/base-currency');
            if (response.ok) {
                const data = await response.json();
                setBaseCurrency(data.base_currency);
            } else {
                setBaseCurrency('USD');
            }
        } catch (err) {
            console.error('Error fetching base currency:', err);
            setBaseCurrency('USD');
        }
    };

    const fetchUserRoles = async (userId: string) => {
        try {
            const response = await apiFetch(`/api/users/${userId}`);
            if (!response.ok) return;
            const data = await response.json();
            const roles = Array.isArray(data.roles) ? data.roles : [];
            const permissions = Array.isArray(data.permissions) ? data.permissions : [];
            const modules = Array.isArray(data.modules) ? data.modules : [];
            setFormData((prev) => ({ ...prev, roles, permissions, modules }));
        } catch (err) {
            console.error('Error fetching user roles:', err);
        }
    };


    const handleSave = async () => {
        try {
            setSaving(true);

            // Update employee
            const employeeResponse = await apiFetch(`/api/people/employees/${employeeId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email || null,
                    phone_number: formData.phone_number || null,
                    tax_id: formData.tax_id || null,
                    bank_account_number: formData.bank_account_number || null,
                    manager_id: formData.manager_id || null,
                    is_active: formData.is_active
                })
            });

            if (!employeeResponse.ok) {
                throw new Error('Failed to update employee');
            }

            // Update user admin settings
            if (employee?.user) {
                const userPayload: Record<string, unknown> = {
                    cost_center_id: formData.cost_center_id || null,
                };
                if (canManageEmployeeAccess) {
                    userPayload.is_admin = formData.is_admin;
                    userPayload.roles = formData.roles;
                    userPayload.permissions = formData.permissions;
                    userPayload.modules = formData.modules;
                }

                const userResponse = await apiFetch(`/api/users/${employee.user.id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(userPayload)
                });

                if (!userResponse.ok) {
                    throw new Error('Failed to update user settings');
                }
            }

            await fetchEmployee();
            setEditing(false);
        } catch (err) {
            console.error('Error saving employee:', err);
            setError(err instanceof Error ? err.message : 'Failed to save employee');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditing(false);
        if (employee) {
            setFormData({
                first_name: employee.first_name,
                last_name: employee.last_name,
                email: employee.email || '',
                phone_number: employee.phone_number || '',
                tax_id: employee.tax_id || '',
                bank_account_number: employee.bank_account_number || '',
                manager_id: employee.manager_id || '',
                is_active: employee.is_active,
                is_admin: employee.user?.is_admin || false,
                cost_center_id: employee.user?.cost_center_id || '',
                roles: employee.user?.roles || [],
                permissions: employee.user?.permissions || [],
                modules: employee.user?.modules || [],
            });
        }
    };

    // Contract management functions
    const resetContractForm = () => {
        setContractFormData({
            employee_id: employeeId as string,
            company_id: '',
            taxation_type: 'full_time_employee',
            default_cost_center_id: '',
            start_date: '',
            end_date: '',
            monthly_salary: '',
            is_active: true
        });
        setJobAssignments([]);
        setContractError(null);
    };

    const openCreateDialog = () => {
        resetContractForm();
        setEditingContract(null);
        setShowCreateDialog(true);
    };

    const openEditDialog = (contract: EmploymentContract) => {
        setContractFormData({
            employee_id: employeeId as string,
            company_id: contract.company_id, // Will be populated from API
            taxation_type: contract.taxation_type as 'full_time_employee' | 'daily_employee' | 'student',
            default_cost_center_id: contract.default_cost_center_id || '',
            start_date: contract.start_date.split('T')[0],
            end_date: contract.end_date ? contract.end_date.split('T')[0] : '',
            monthly_salary: contract.monthly_salary ? contract.monthly_salary.toString() : '',
            is_active: contract.is_active
        });
        setJobAssignments(
            (contract.job_assignments || []).map(ja => ({
                job_id: ja.job_id,
                job_level_id: ja.job_level_id,
                cost_center_id: ''
            }))
        );
        setEditingContract(contract);
        setShowCreateDialog(true);
    };

    // Job assignment handlers
    const addJobAssignment = () => {
        setJobAssignments([...jobAssignments, {
            job_id: '',
            job_level_id: '',
            cost_center_id: ''
        }]);
    };

    const removeJobAssignment = (index: number) => {
        setJobAssignments(jobAssignments.filter((_, i) => i !== index));
    };

    const updateJobAssignment = (index: number, field: keyof JobAssignmentFormData, value: string) => {
        const updated = [...jobAssignments];
        updated[index] = { ...updated[index], [field]: value };

        // If job changed, reset job_level_id
        if (field === 'job_id') {
            updated[index].job_level_id = '';
        }

        setJobAssignments(updated);
    };

    const getAvailableLevelsForJob = (jobId: string): TimesheetJobLevel[] => {
        const job = jobs.find(j => j.id === jobId);
        return job?.levels || [];
    };

    const getHourlyRateForTaxationType = (level: TimesheetJobLevel, taxationType: 'full_time_employee' | 'daily_employee' | 'student'): number => {
        if (!level.rates || level.rates.length === 0) {
            // Fallback to legacy hourly_rate field
            return level.hourly_rate || 0;
        }

        // Find the rate for the specific taxation type
        const rate = level.rates.find(r => r.taxation_type === taxationType);
        if (rate && rate.hourly_rate !== null) {
            return rate.hourly_rate;
        }

        // Fallback to full_time_employee rate
        const fullTimeRate = level.rates.find(r => r.taxation_type === 'full_time_employee');
        if (fullTimeRate && fullTimeRate.hourly_rate !== null) {
            return fullTimeRate.hourly_rate;
        }

        // Final fallback to legacy field
        return level.hourly_rate || 0;
    };

    const toggleRole = (roleValue: string) => {
        setFormData((prev) => {
            const hasRole = prev.roles.includes(roleValue);
            const updated = hasRole
                ? prev.roles.filter((r) => r !== roleValue)
                : [...prev.roles, roleValue];
            return { ...prev, roles: updated };
        });
    };

    const handlePrimaryRoleChange = (value: string) => {
        if (value === 'none') {
            setFormData((prev) => ({ ...prev, roles: [] }));
        } else {
            setFormData((prev) => ({ ...prev, roles: [value] }));
        }
    };

    const togglePermission = (permValue: string) => {
        setFormData((prev) => {
            const has = prev.permissions.includes(permValue);
            const permissions = has
                ? prev.permissions.filter((p) => p !== permValue)
                : [...prev.permissions, permValue];
            return { ...prev, permissions };
        });
    };

    const toggleModule = (moduleValue: string) => {
        setFormData((prev) => {
            const has = prev.modules.includes(moduleValue);
            const modules = has
                ? prev.modules.filter((m) => m !== moduleValue)
                : [...prev.modules, moduleValue];
            return { ...prev, modules };
        });
    };

    const validateContractForm = (): string | null => {
        if (!contractFormData.employee_id) return t('contracts.validation.selectEmployee', 'people');
        if (!contractFormData.start_date) return t('contracts.validation.selectStartDate', 'people');

        // Monthly salary is mandatory for Full Time employees
        if (contractFormData.taxation_type === 'full_time_employee' && !contractFormData.monthly_salary) {
            return 'Monthly salary is required for Full Time employees';
        }

        // Job assignments are only required for active contracts
        if (contractFormData.is_active && jobAssignments.length === 0) {
            return t('contracts.validation.addJobAssignment', 'people');
        }

        for (let i = 0; i < jobAssignments.length; i++) {
            const assignment = jobAssignments[i];
            if (!assignment.job_id) return t('contracts.validation.jobAssignmentSelectJob', 'people', { number: i + 1 });
            if (!assignment.job_level_id) return t('contracts.validation.jobAssignmentSelectLevel', 'people', { number: i + 1 });

            // Check for duplicate jobs
            const duplicateJob = jobAssignments.find((ja, idx) =>
                idx !== i && ja.job_id === assignment.job_id
            );
            if (duplicateJob) {
                const job = jobs.find(j => j.id === assignment.job_id);
                return t('contracts.validation.duplicateJob', 'people', { jobTitle: job?.title });
            }
        }

        return null;
    };

    const handleContractSubmit = async () => {
        const validationError = validateContractForm();
        if (validationError) {
            setContractError(validationError);
            return;
        }

        setSubmitting(true);
        setContractError(null);

        try {
            const submitData: any = {
                employee_id: contractFormData.employee_id,
                company_id: contractFormData.company_id,
                taxation_type: contractFormData.taxation_type,
                default_cost_center_id: contractFormData.default_cost_center_id || null,
                start_date: new Date(contractFormData.start_date).toISOString(),
                end_date: contractFormData.end_date ? new Date(contractFormData.end_date).toISOString() : null,
                is_active: contractFormData.is_active,
                job_assignments: jobAssignments.map(ja => ({
                    job_id: ja.job_id,
                    job_level_id: ja.job_level_id,
                    cost_center_id: ja.cost_center_id || null
                }))
            };

            // Only include monthly_salary for full_time_employee
            if (contractFormData.taxation_type === 'full_time_employee' && contractFormData.monthly_salary) {
                submitData.monthly_salary = parseFloat(contractFormData.monthly_salary);
            }

            const url = editingContract
                ? `/api/people/contracts/${editingContract.id}`
                : '/api/people/contracts/';
            const method = editingContract ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submitData)
            });

            if (response.ok) {
                await fetchEmployee();
                setShowCreateDialog(false);
                resetContractForm();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to ${editingContract ? 'update' : 'create'} contract`);
            }
        } catch (err) {
            console.error('Error submitting contract:', err);
            setContractError(err instanceof Error ? err.message : `Failed to ${editingContract ? 'update' : 'create'} contract`);
        } finally {
            setSubmitting(false);
        }
    };

    const formatTaxationType = (type: string) => {
        if (type === 'full_time_employee') return t('contracts.taxationType.fullTime', 'people');
        if (type === 'daily_employee') return t('contracts.taxationType.daily', 'people');
        if (type === 'student') return t('contracts.taxationType.student', 'people');
        return type;
    };

    const getCostCenterName = (costCenterId: string | null) => {
        if (!costCenterId) return t('contracts.noCostCenter', 'people');
        const costCenter = costCenters.find(cc => cc.id === costCenterId);
        return costCenter ? costCenter.name : t('contracts.unknownCostCenter', 'people');
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </Layout>
        );
    }

    if (error || !employee) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Button variant="ghost" onClick={() => navigate({ to: '/people/employees' })}>
                        <ArrowLeftIcon className="w-4 h-4 mr-2" />
                        {t('navigation.backTo', 'common')} {t('navigation.employees', 'common')}
                    </Button>
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error || t('employees.notFound', 'people')}
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={() => navigate({ to: '/people/employees' })}>
                            <ArrowLeftIcon className="w-4 h-4 mr-2" />
                            {t('actions.back', 'common')}
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">
                                {employee.first_name} {employee.last_name}
                            </h1>
                            <p className="text-muted-foreground">
                                {employee.email || t('employees.noEmail', 'people')}
                            </p>
                        </div>
                        <Badge variant={employee.is_active ? "default" : "secondary"}>
                            {employee.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                        </Badge>
                    </div>

                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList>
                        <TabsTrigger value="details" disabled={editing}>{t('employees.tabs.details', 'people')}</TabsTrigger>
                        <TabsTrigger value="access" disabled={editing}>{t('employees.tabs.access', 'people')}</TabsTrigger>
                        <TabsTrigger value="contracts" disabled={editing}>{t('employees.tabs.contracts', 'people')}</TabsTrigger>
                        <TabsTrigger value="timesheets" disabled={editing}>{t('employees.tabs.timesheets', 'people')}</TabsTrigger>
                        <TabsTrigger value="timeoff" disabled={editing}>{t('employees.tabs.timeOff', 'people')}</TabsTrigger>
                    </TabsList>

                    {/* Details Tab */}
                    <TabsContent value="details" className="space-y-6">
                        <div className="w-full flex justify-end gap-2 mb-2">
                            {editing ? (
                                <>
                                    <Button variant="outline" onClick={handleCancel} disabled={saving}>
                                        <XMarkIcon className="w-4 h-4 mr-2" />
                                        {t('actions.cancel', 'common')}
                                    </Button>
                                    <Button onClick={handleSave} disabled={saving}>
                                        <CheckIcon className="w-4 h-4 mr-2" />
                                        {saving ? t('actions.saving', 'common') : t('actions.save', 'common')}
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={() => {
                                    setEditing(true);
                                    setActiveTab('details');
                                }}>
                                    <PencilIcon className="w-4 h-4 mr-2" />
                                    {t('actions.edit', 'common')}
                                </Button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Personal Information */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('employees.personalInformation', 'people')}</CardTitle>
                                    <CardDescription>{t('employees.personalInformationDesc', 'people')}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">

                                    <div>
                                        <Label htmlFor="first_name">{t('forms.firstName', 'common')}</Label>
                                        {editing ? (
                                            <Input
                                                id="first_name"
                                                value={formData.first_name}
                                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                            />
                                        ) : (
                                            <p className="text-sm mt-1">{employee.first_name}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="last_name">{t('forms.lastName', 'common')}</Label>
                                        {editing ? (
                                            <Input
                                                id="last_name"
                                                value={formData.last_name}
                                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                            />
                                        ) : (
                                            <p className="text-sm mt-1">{employee.last_name}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="email">{t('forms.email_address', 'common')}</Label>
                                        {editing ? (
                                            <Input
                                                id="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            />
                                        ) : (
                                            <p className="text-sm mt-1">{employee.email || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="phone_number">{t('forms.phoneNumber', 'common')}</Label>
                                        {editing ? (
                                            <Input
                                                id="phone_number"
                                                value={formData.phone_number}
                                                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                            />
                                        ) : (
                                            <p className="text-sm mt-1">{employee.phone_number || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="tax_id">{t('employees.taxId', 'people')}</Label>
                                        {editing ? (
                                            <Input
                                                id="tax_id"
                                                value={formData.tax_id}
                                                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                                                placeholder="e.g., 1234567890"
                                            />
                                        ) : (
                                            <p className="text-sm mt-1">{employee.tax_id || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="bank_account_number">{t('employees.bankAccount', 'people')}</Label>
                                        {editing ? (
                                            <Input
                                                id="bank_account_number"
                                                value={formData.bank_account_number}
                                                onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                                            />
                                        ) : (
                                            <p className="text-sm mt-1">{employee.bank_account_number || '-'}</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* System Settings */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('employees.systemSettings', 'people')}</CardTitle>
                                    <CardDescription>{t('employees.systemSettingsDesc', 'people')}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <Label htmlFor="manager_id">{t('employees.manager', 'people')}</Label>
                                        {editing ? (
                                            <Select
                                                value={formData.manager_id || "none"}
                                                onValueChange={(value) => setFormData({ ...formData, manager_id: value === "none" ? "" : value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t('employees.selectManager', 'people')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">{t('employees.noManager', 'people')}</SelectItem>
                                                    {allEmployees.filter(e => e.id !== employee.id).map((emp) => (
                                                        <SelectItem key={emp.id} value={emp.id}>
                                                            {emp.first_name} {emp.last_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <p className="text-sm mt-1">
                                                {employee.manager ? `${employee.manager.first_name} ${employee.manager.last_name}` : '-'}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="cost_center_id">{t('employees.costCenter', 'people')}</Label>
                                        {editing ? (
                                            <Select
                                                value={formData.cost_center_id || "none"}
                                                onValueChange={(value) => setFormData({ ...formData, cost_center_id: value === "none" ? "" : value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t('employees.selectCostCenter', 'people')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">{t('employees.noCostCenter', 'people')}</SelectItem>
                                                    {costCenters.map((cc) => (
                                                        <SelectItem key={cc.id} value={cc.id}>
                                                            {cc.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <p className="text-sm mt-1">
                                                {costCenters.find(cc => cc.id === employee.user?.cost_center_id)?.name || '-'}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="roles_primary">{t('employees.role', 'people')}</Label>
                                        {editing && canManageEmployeeAccess ? (
                                            <Select
                                                value={formData.roles[0] || "none"}
                                                onValueChange={handlePrimaryRoleChange}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t('employees.selectRole', 'people')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">{t('employees.noRole', 'people')}</SelectItem>
                                                    {availableRoles.map((role) => (
                                                        <SelectItem key={role.value} value={role.value}>
                                                            {role.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {(formData.roles.length ? formData.roles : employee.user?.roles || []).length > 0 ? (
                                                    (formData.roles.length ? formData.roles : employee.user?.roles || []).map((role) => (
                                                        <Badge key={role} variant="secondary">{role}</Badge>
                                                    ))
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">{t('employees.noRoleAssigned', 'people')}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {editing && canManageEmployeeAccess ? (
                                            <>
                                                <input
                                                    type="checkbox"
                                                    id="is_admin"
                                                    checked={formData.is_admin}
                                                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                                                    className="rounded"
                                                />
                                                <Label htmlFor="is_admin">{t('employees.administrator', 'people')}</Label>
                                            </>
                                        ) : (
                                            <div>
                                                <Label>{t('employees.administrator', 'people')}</Label>
                                                <p className="text-sm mt-1">{employee.user?.is_admin ? t('common.yes', 'common') : t('common.no', 'common')}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {editing ? (
                                            <>
                                                <input
                                                    type="checkbox"
                                                    id="is_active"
                                                    checked={formData.is_active}
                                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                    className="rounded"
                                                />
                                                <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                                            </>
                                        ) : (
                                            <div>
                                                <Label>{t('forms.status', 'common')}</Label>
                                                <p className="text-sm mt-1">{employee.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}</p>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Groups */}
                        {employee.groups && employee.groups.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('employees.groups', 'people')}</CardTitle>
                                    <CardDescription>{t('employees.groupsDesc', 'people')}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex flex-wrap gap-2">
                                        {employee.groups.map((group) => (
                                            <Badge key={group.id} variant="outline">
                                                {group.name}
                                            </Badge>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Tags */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('tags.tags', 'common')}</CardTitle>
                                <CardDescription>{t('employees.tagsDesc', 'people')}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <TagSelector
                                    entityType="employees"
                                    entityId={employeeId as string}
                                    selectedTags={employeeTags}
                                    onChange={(tags) => {
                                        setEmployeeTags(tags);
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="access">
                        <div className="w-full flex justify-end gap-2 mb-4">
                            {canManageEmployeeAccess ? (
                                editing ? (
                                <>
                                    <Button variant="outline" onClick={handleCancel} disabled={saving}>
                                        <XMarkIcon className="w-4 h-4 mr-2" />
                                        {t('actions.cancel', 'common')}
                                    </Button>
                                    <Button onClick={handleSave} disabled={saving}>
                                        <CheckIcon className="w-4 h-4 mr-2" />
                                        {saving ? t('actions.saving', 'common') : t('actions.save', 'common')}
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={() => setEditing(true)}>
                                    <PencilIcon className="w-4 h-4 mr-2" />
                                    {t('actions.edit', 'common')}
                                </Button>
                            )
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    You do not have permission to edit employee access and roles.
                                </p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('employees.access.rolesTitle', 'people')}</CardTitle>
                                    <CardDescription>{t('employees.access.rolesDescription', 'people')}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {availableRoles.map((role) => (
                                        <label key={role.value} className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={formData.roles.includes(role.value)}
                                                onChange={() => toggleRole(role.value)}
                                                disabled={!editing || !canManageEmployeeAccess}
                                                className="mt-1 rounded"
                                            />
                                            <div>
                                                <p className="font-medium">{role.label}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {t('employees.access.roleGrantDescription', 'people', { role: role.label })}
                                                </p>
                                            </div>
                                        </label>
                                    ))}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('employees.access.permissionsTitle', 'people')}</CardTitle>
                                    <CardDescription>
                                        {t('employees.access.permissionsDescription', 'people')}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
                                    {availablePermissions.map((perm) => (
                                        <label key={perm.value} className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={formData.permissions.includes(perm.value)}
                                                onChange={() => togglePermission(perm.value)}
                                                disabled={!editing || !canManageEmployeeAccess}
                                                className="mt-1 rounded"
                                            />
                                            <div>
                                                <p className="font-medium text-foreground">{perm.label}</p>
                                                <p className="text-xs text-muted-foreground">{perm.value}</p>
                                            </div>
                                        </label>
                                    ))}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Modules</CardTitle>
                                    <CardDescription>
                                        Choose which main-menu modules this user can access.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {availableModules.map((module) => (
                                        <label key={module.value} className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={formData.modules.includes(module.value)}
                                                onChange={() => toggleModule(module.value)}
                                                disabled={!editing || !canManageEmployeeAccess}
                                                className="mt-1 rounded"
                                            />
                                            <div>
                                                <p className="font-medium">{module.label}</p>
                                                <p className="text-sm text-muted-foreground">{module.description}</p>
                                                <p className="text-xs text-muted-foreground">{module.value}</p>
                                            </div>
                                        </label>
                                    ))}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    <TabsContent value="contracts">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <CardTitle>{t('contracts.title', 'people')}</CardTitle>
                                        <CardDescription>{t('contracts.description', 'people')}</CardDescription>
                                    </div>
                                    <Button onClick={openCreateDialog}>
                                        <PlusIcon className="w-4 h-4 mr-2" />
                                        {t('contracts.newContract', 'people')}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {employee.contracts && employee.contracts.length > 0 ? (
                                    <div className="bg-white">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>{t('contracts.jobAssignments', 'people')}</TableHead>
                                                    <TableHead>{t('contracts.taxationType.title', 'people')}</TableHead>
                                                    <TableHead>{t('contracts.company', 'people')}</TableHead>
                                                    <TableHead>{t('contracts.defaultCostCenter', 'people')}</TableHead>
                                                    <TableHead>{t('contracts.startDate', 'people')}</TableHead>
                                                    <TableHead>{t('contracts.endDate', 'people')}</TableHead>
                                                    <TableHead>{t('forms.status', 'common')}</TableHead>
                                                    <TableHead className="w-[100px]"></TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {employee.contracts.map((contract) => (
                                                    <TableRow key={contract.id} className={`${!contract.is_active ? 'opacity-60 bg-muted/20' : ''}`}>
                                                        <TableCell>
                                                            {contract.job_assignments && contract.job_assignments.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {contract.job_assignments.map((assignment) => {
                                                                        const job = jobs.find(j => j.id === assignment.job_id);
                                                                        const level = job?.levels?.find(l => l.id === assignment.job_level_id);
                                                                        return (
                                                                            <Badge key={assignment.id} variant="secondary">
                                                                                {job ? job.title : t('contracts.job', 'people')}{level ? ` - ${level.level_name}` : ''}
                                                                            </Badge>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted-foreground text-sm">{t('contracts.noAssignments', 'people')}</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{contract.taxation_type === 'full_time_employee' ? t('contracts.taxationType.fullTime', 'people') : contract.taxation_type === 'daily_employee' ? t('contracts.taxationType.daily', 'people') : t('contracts.taxationType.student', 'people')}</TableCell>
                                                        <TableCell>{companies.find(c => c.id === contract.company_id)?.name || '-'}</TableCell>
                                                        <TableCell>{costCenters.find(cc => cc.id === contract.default_cost_center_id)?.name || '-'}</TableCell>
                                                        <TableCell>{new Date(contract.start_date).toLocaleDateString()}</TableCell>
                                                        <TableCell>{contract.end_date ? new Date(contract.end_date).toLocaleDateString() : t('contracts.openEnded', 'people')}</TableCell>
                                                        <TableCell>
                                                            <Badge variant={contract.is_active ? 'default' : 'secondary'}>
                                                                {contract.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEditDialog(contract);
                                                                }}
                                                            >
                                                                <PencilIcon className="w-4 h-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground">{t('contracts.noContracts', 'people')}</p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="timesheets">
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('timesheets.title', 'people')}</CardTitle>
                                <CardDescription>{t('timesheets.description', 'people')}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">{t('timesheets.comingSoon', 'people')}</p>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="timeoff">
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('timeoff.title', 'people')}</CardTitle>
                                <CardDescription>{t('timeoff.description', 'people')}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">{t('timeoff.comingSoon', 'people')}</p>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Create/Edit Contract Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateDialog(false);
                    resetContractForm();
                }
            }}>
                <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingContract ? t('contracts.editContract', 'people') : t('contracts.createContract', 'people')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6">
                        {/* Error Display */}
                        {contractError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                                {contractError}
                            </div>
                        )}

                        {/* Basic Contract Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="company_id">{t('contracts.company', 'people')} *</Label>
                                <Select
                                    value={contractFormData.company_id}
                                    onValueChange={(value) => setContractFormData({ ...contractFormData, company_id: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('contracts.selectCompany', 'people')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companies.map((company) => (
                                            <SelectItem key={company.id} value={company.id}>
                                                {company.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="taxation_type">{t('contracts.taxationType.title', 'people')} *</Label>
                                <Select
                                    value={contractFormData.taxation_type}
                                    onValueChange={(value: 'full_time_employee' | 'daily_employee' | 'student') => {
                                        setContractFormData({ ...contractFormData, taxation_type: value, monthly_salary: '' });
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="full_time_employee">{t('contracts.taxationType.fullTime', 'people')}</SelectItem>
                                        <SelectItem value="daily_employee">{t('contracts.taxationType.daily', 'people')}</SelectItem>
                                        <SelectItem value="student">{t('contracts.taxationType.student', 'people')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="default_cost_center_id">{t('contracts.defaultCostCenter', 'people')}</Label>
                                <Select
                                    value={contractFormData.default_cost_center_id || "none"}
                                    onValueChange={(value) =>
                                        setContractFormData({ ...contractFormData, default_cost_center_id: value === "none" ? "" : value })
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('contracts.selectCostCenter', 'people')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">{t('contracts.noDefaultCostCenter', 'people')}</SelectItem>
                                        {costCenters.map((costCenter) => (
                                            <SelectItem key={costCenter.id} value={costCenter.id}>
                                                {costCenter.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Monthly Salxatioield - only for Full Time employees */}
                            {contractFormData.taxation_type === 'full_time_employee' && (
                                <div>
                                    <Label htmlFor="monthly_salary">Monthly Salary ({baseCurrency}) *</Label>
                                    <Input
                                        id="monthly_salary"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={contractFormData.monthly_salary}
                                        onChange={(e) => setContractFormData({ ...contractFormData, monthly_salary: e.target.value })}
                                        placeholder="Gross monthly salary"
                                    />
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Net transfer (after taxes): {contractFormData.monthly_salary ? formatCurrency(parseFloat(contractFormData.monthly_salary) * 0.665, getCurrencySymbol(baseCurrency)) : '-'}
                                    </p>
                                </div>
                            )}

                            <div>
                                <Label htmlFor="start_date">{t('contracts.startDate', 'people')} *</Label>
                                <Input
                                    id="start_date"
                                    type="date"
                                    value={contractFormData.start_date}
                                    onChange={(e) => setContractFormData({ ...contractFormData, start_date: e.target.value })}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="end_date">{t('contracts.endDate', 'people')}</Label>
                                <Input
                                    id="end_date"
                                    type="date"
                                    value={contractFormData.end_date}
                                    onChange={(e) => setContractFormData({ ...contractFormData, end_date: e.target.value })}
                                />
                            </div>

                            <div className="flex items-center space-x-2 pt-6">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={contractFormData.is_active}
                                    onChange={(e) => setContractFormData({ ...contractFormData, is_active: e.target.checked })}
                                    className="rounded"
                                />
                                <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                            </div>
                        </div>

                        {/* Job Assignments Section */}
                        <div className="border-t pt-4">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold">{t('contracts.jobAssignments', 'people')}</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {t('contracts.jobAssignmentsDescription', 'people')}
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addJobAssignment}
                                >
                                    <PlusIcon className="w-4 h-4 mr-1" />
                                    {t('contracts.addJob', 'people')}
                                </Button>
                            </div>

                            {jobAssignments.length === 0 ? (
                                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                                    <p className="text-muted-foreground mb-2">{t('contracts.noJobAssignmentsYet', 'people')}</p>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addJobAssignment}
                                    >
                                        <PlusIcon className="w-4 h-4 mr-1" />
                                        {t('contracts.addFirstJob', 'people')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {jobAssignments.map((assignment, index) => {
                                        const availableLevels = getAvailableLevelsForJob(assignment.job_id);

                                        return (
                                            <div key={index} className="flex gap-2 items-start p-3 border rounded-lg bg-muted/20">
                                                <div className="flex-1 grid grid-cols-3 gap-2">
                                                    <div>
                                                        <Label className="text-xs">{t('contracts.job', 'people')}</Label>
                                                        <Select
                                                            value={assignment.job_id}
                                                            onValueChange={(value) => updateJobAssignment(index, 'job_id', value)}
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue placeholder={t('contracts.selectJob', 'people')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {jobs.map((job) => (
                                                                    <SelectItem key={job.id} value={job.id}>
                                                                        {job.title}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <Label className="text-xs">{t('contracts.level', 'people')}</Label>
                                                        <Select
                                                            key={`${index}-${contractFormData.taxation_type}`}
                                                            value={assignment.job_level_id}
                                                            onValueChange={(value) => updateJobAssignment(index, 'job_level_id', value)}
                                                            disabled={!assignment.job_id}
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue placeholder={t('contracts.selectLevel', 'people')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {availableLevels.map((level) => {
                                                                    const hourlyRate = getHourlyRateForTaxationType(level, contractFormData.taxation_type);
                                                                    return (
                                                                        <SelectItem key={level.id} value={level.id}>
                                                                            {level.level_name} ({formatCurrency(hourlyRate, getCurrencySymbol(baseCurrency))}/hr)
                                                                        </SelectItem>
                                                                    );
                                                                })}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <Label className="text-xs">{t('contracts.costCenterOverride', 'people')}</Label>
                                                        <Select
                                                            value={assignment.cost_center_id || "none"}
                                                            onValueChange={(value) =>
                                                                updateJobAssignment(index, 'cost_center_id', value === "none" ? "" : value)
                                                            }
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue placeholder={t('contracts.useDefault', 'people')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="none">{t('contracts.useDefault', 'people')}</SelectItem>
                                                                {costCenters.map((cc) => (
                                                                    <SelectItem key={cc.id} value={cc.id}>
                                                                        {cc.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeJobAssignment(index)}
                                                    className="mt-5"
                                                >
                                                    <TrashIcon className="w-4 h-4 text-red-500" />
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Dialog Footer */}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateDialog(false);
                                    resetContractForm();
                                }}
                            >
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button
                                type="button"
                                onClick={handleContractSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (editingContract ? t('actions.updating', 'common') : t('actions.creating', 'common')) : (editingContract ? t('actions.update', 'common') : t('actions.create', 'common'))}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

export const Route = createFileRoute('/people/employees/$employeeId')({
    component: EmployeeDetailPage,
})
